import { getToken } from '@/api/utils/token.holder';
import {
  Configuration as ApiConfiguration,
  ConfigurationParameters as ApiConfigurationParameters,
  FetchParams,
  Middleware,
  RequestContext,
  ResponseContext,
} from '@/generated/api/fps/api';
import {
  Configuration as CoreConfiguration,
  ConfigurationParameters as CoreConfigurationParameters,
} from '@/generated/api/fps/core';
import { saveForbidden } from '@/model/auth/actions';
import { getStore } from '@/model/store.holder';

const modeHolder: { mode: 'cookie' | 'header' } = { mode: 'cookie' };
export const setAuthMode = (mode: 'cookie' | 'header'): void => {
  modeHolder.mode = mode;
};

export interface ResponseErrorData {
  headers: Headers;
  ok: boolean;
  status: number;
  statusText: string;
  // if json
  data?: { code: string; message: string; serviceName?: string; status: number; timestamp: Date };
}

export class ResponseError extends Error {
  constructor(data: ResponseErrorData) {
    super('response error');
    // eslint-disable-next-line no-underscore-dangle
    this._data = data;
  }

  private _data: ResponseErrorData;

  get data(): ResponseErrorData {
    // eslint-disable-next-line no-underscore-dangle
    return this._data;
  }
}

export const parseErrorResponse = async (error: Response): Promise<ResponseError> => {
  let data;
  try {
    data = await error.json();
  } catch (e) {
    try {
      console.warn(`unable to parse error response ${e}`);
    } catch (e2) {
      console.warn(`unable to parse error response ${e.toString()}`);
    }
  }
  return new ResponseError({
    data,
    headers: error?.headers,
    ok: error?.ok,
    status: error?.status,
    statusText: error?.statusText,
  });
};

export const parseErrorMiddleware: Middleware = {
  async post(context: ResponseContext): Promise<Response | void> {
    if (!context.response.ok) {
      let error;
      try {
        error = await parseErrorResponse(context.response);
      } catch (e) {
        console.warn(e);
        error = context.response;
      }
      throw error;
    }
    return context.response;
  },
};

export const sessionTokenMiddleware: Middleware = {
  async pre(context: RequestContext): Promise<FetchParams | void> {
    const credentials: RequestCredentials | undefined = modeHolder.mode === 'cookie' ? 'include' : undefined;
    const token = getToken();
    if (!token && !credentials) {
      return context;
    }

    return {
      ...context,
      init: {
        ...context.init,
        credentials,
        headers: token ? { ...(context.init.headers || {}), authorization: `Bearer ${token}` } : context.init.headers,
      },
    };
  },
};

// T - workaround for multiple same (!!!) instances of runtime from openapi-codegen
export const publicConfigurationFactory = <T extends CoreConfiguration | ApiConfiguration>(
  basePath: string,
  params?: CoreConfigurationParameters | ApiConfigurationParameters,
): T =>
  new CoreConfiguration({
    ...params,
    middleware: [parseErrorMiddleware],
    basePath,
  }) as T;

export const authNoForbiddenConfigurationFactory = <T extends CoreConfiguration | ApiConfiguration>(
  basePath: string,
  params?: CoreConfigurationParameters | ApiConfigurationParameters,
): T =>
  new CoreConfiguration({
    middleware: [sessionTokenMiddleware, parseErrorMiddleware],
    ...params,
    basePath,
  }) as T;

export const authMiddleware: Middleware = {
  async post(context: ResponseContext): Promise<Response | void> {
    if (context.response?.status === 403) {
      getStore()
        ?.dispatch(saveForbidden(true).pure);
      let error;
      try {
        error = await parseErrorResponse(context.response);
      } catch (e) {
        console.warn(e);
        error = new Error('Unauthorized');
      }
      throw error;
    }
    return context.response;
  },
};

const apiPath = window.env.FPS_API_URL.replace(/\/$/, '');

export const apiBasePath = `${apiPath}`;
export const coreBasePath = `${apiPath}/core`;

export const authConfigurationFactory = <T extends CoreConfiguration | ApiConfiguration>(
  basePath: string,
  params?: CoreConfigurationParameters | ApiConfigurationParameters,
): T =>
  new CoreConfiguration({
    middleware: [sessionTokenMiddleware, authMiddleware, parseErrorMiddleware],
    ...params,
    basePath,
  }) as T;

type ConfigurationType = 'auth' | 'public' | 'no-forbidden';

const typedConfigurationFactory = <T extends CoreConfiguration | ApiConfiguration,
  P = CoreConfigurationParameters | ApiConfigurationParameters>(
  basePath: string,
  type: ConfigurationType,
  params?: P,
): T => {
  if (type === 'public') {
    return publicConfigurationFactory(basePath, params);
  }
  if (type === 'auth') {
    return authConfigurationFactory(basePath, params);
  }
  if (type === 'no-forbidden') {
    return authNoForbiddenConfigurationFactory(basePath, params);
  }
  throw new Error(`unknown configuration type ${type}`);
};

export const apiConfigurationFactory = (
  type: ConfigurationType,
  params?: ApiConfigurationParameters,
): ApiConfiguration => typedConfigurationFactory(apiBasePath, type, params);

export const coreConfigurationFactory = (
  type: ConfigurationType,
  params?: CoreConfigurationParameters,
): CoreConfiguration => typedConfigurationFactory(coreBasePath, type, params);
